import React, { useCallback, useEffect, useState } from "react";
import { toast } from "react-toastify";
import api from "../../api/axios";
import { superadminErrorMessage, useCurrentUser } from "../../utilities/currentUser";
import { isSuperadminUser } from "../../utils/leadershipRoles";

const STATUS_BADGE = {
  pending: "bg-warning text-dark",
  approved: "bg-success",
  rejected: "bg-danger",
  cancelled: "bg-secondary",
};

const FILTERS = [
  { value: "", label: "All" },
  { value: "pending", label: "Pending" },
  { value: "approved", label: "Approved" },
  { value: "rejected", label: "Rejected" },
  { value: "cancelled", label: "Cancelled" },
];

const formatDate = (value) => (value ? new Date(value).toLocaleString() : "—");

/**
 * Refund requests of the selected company. Superadmin: approve (records the full refund) or reject
 * with a note. Finance Director / Admin: follow the status and cancel their own pending requests.
 */
export default function RefundRequestsPage() {
  const { user } = useCurrentUser();
  const isSuperadmin = isSuperadminUser(user);
  const [status, setStatus] = useState("");
  const [rows, setRows] = useState([]);
  const [pendingCount, setPendingCount] = useState(0);
  const [loading, setLoading] = useState(true);
  const [busyId, setBusyId] = useState(null);
  const [rejectingId, setRejectingId] = useState(null);
  const [rejectNote, setRejectNote] = useState("");

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.get("/api/refund-requests", { params: status ? { status } : {} });
      setRows(res.data?.data || []);
      setPendingCount(res.data?.pending_count || 0);
    } catch (err) {
      toast.error(err?.response?.data?.message || "Could not load refund requests.");
    } finally {
      setLoading(false);
    }
  }, [status]);

  useEffect(() => {
    load();
  }, [load]);

  const act = async (row, action, payload, successMessage) => {
    if (busyId) return false;
    setBusyId(row.id);
    try {
      await api.post(`/api/refund-requests/${row.id}/${action}`, payload || {});
      toast.success(successMessage);
      await load();
      return true;
    } catch (err) {
      toast.error(
        action === "cancel"
          ? err?.response?.data?.message || "Could not cancel the request."
          : superadminErrorMessage(err, "Could not complete this action.")
      );
      await load();
      return false;
    } finally {
      setBusyId(null);
    }
  };

  const approve = (row) => {
    const ok = window.confirm(
      `Record a full refund of ৳${Number(row.amount).toLocaleString()} (reference ${row.refund_reference})? This records a refund already made outside the system.`
    );
    if (ok) act(row, "approve", {}, "Refund approved and recorded.");
  };

  const reject = async (row) => {
    if (rejectNote.trim().length < 3) {
      toast.error("Add a note explaining the rejection.");
      return;
    }
    const done = await act(row, "reject", { note: rejectNote.trim() }, "Refund request rejected.");
    if (done) {
      setRejectingId(null);
      setRejectNote("");
    }
  };

  return (
    <div className="container-fluid">
      <div className="d-flex flex-wrap align-items-center justify-content-between mb-3 gap-2">
        <h3 className="fw-bold text-primary mb-0">
          Refund Requests
          {isSuperadmin && pendingCount > 0 && (
            <span className="badge bg-warning text-dark ms-2">{pendingCount} pending</span>
          )}
        </h3>
        <div className="btn-group" role="group" aria-label="Filter by status">
          {FILTERS.map((f) => (
            <button
              key={f.value || "all"}
              type="button"
              className={`btn btn-sm ${status === f.value ? "btn-primary" : "btn-outline-primary"}`}
              onClick={() => setStatus(f.value)}
            >
              {f.label}
            </button>
          ))}
        </div>
      </div>

      <p className="text-muted">
        A request asks a Superadmin to record a full refund that was already made outside the system. Approving it
        records the refund; no money is moved.
      </p>

      {loading ? (
        <div>Loading...</div>
      ) : rows.length === 0 ? (
        <div className="alert alert-light border text-center">No refund requests.</div>
      ) : (
        <div className="table-responsive shadow-sm rounded">
          <table className="table table-hover table-bordered align-middle mb-0">
            <thead className="table-primary">
              <tr>
                <th>#</th>
                <th>Booking</th>
                <th>Customer</th>
                <th className="text-end">Amount</th>
                <th>Reference</th>
                <th>Reason</th>
                <th>Requested by</th>
                <th>Status</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((row) => (
                <React.Fragment key={row.id}>
                  <tr>
                    <td>{row.id}</td>
                    <td>
                      {row.property_name || "—"}
                      {row.unit_number ? ` / ${row.unit_number}` : ""}
                      <div className="small text-muted">Booking #{row.booking_id}</div>
                    </td>
                    <td>{row.customer_name || "—"}</td>
                    <td className="text-end fw-bold">৳{Number(row.amount).toLocaleString()}</td>
                    <td>{row.refund_reference}</td>
                    <td style={{ maxWidth: 260 }}>{row.reason}</td>
                    <td>
                      {row.requester_name || "—"}
                      <div className="small text-muted">{formatDate(row.created_at)}</div>
                    </td>
                    <td>
                      <span className={`badge ${STATUS_BADGE[row.status] || "bg-secondary"}`}>{row.status}</span>
                      {row.decided_at && row.status !== "pending" && (
                        <div className="small text-muted">
                          {row.decider_name} · {formatDate(row.decided_at)}
                        </div>
                      )}
                      {row.decision_note && <div className="small">Note: {row.decision_note}</div>}
                    </td>
                    <td className="text-nowrap">
                      {isSuperadmin && row.can_approve && (
                        <>
                          <button
                            type="button"
                            className="btn btn-success btn-sm me-2"
                            disabled={busyId === row.id}
                            onClick={() => approve(row)}
                          >
                            Approve
                          </button>
                          <button
                            type="button"
                            className="btn btn-outline-danger btn-sm"
                            disabled={busyId === row.id}
                            onClick={() => {
                              setRejectingId(rejectingId === row.id ? null : row.id);
                              setRejectNote("");
                            }}
                          >
                            Reject
                          </button>
                        </>
                      )}
                      {isSuperadmin && row.status === "pending" && !row.can_approve && (
                        <span className="small text-muted me-2">Your own request: another Superadmin must approve it.</span>
                      )}
                      {row.can_cancel && (
                        <button
                          type="button"
                          className="btn btn-outline-secondary btn-sm"
                          disabled={busyId === row.id}
                          onClick={() => act(row, "cancel", {}, "Refund request cancelled.")}
                        >
                          Cancel request
                        </button>
                      )}
                    </td>
                  </tr>
                  {rejectingId === row.id && (
                    <tr>
                      <td colSpan={9} className="bg-light">
                        <label className="form-label" htmlFor={`reject-note-${row.id}`}>Rejection note (required)</label>
                        <div className="d-flex gap-2">
                          <input
                            id={`reject-note-${row.id}`}
                            className="form-control"
                            value={rejectNote}
                            onChange={(event) => setRejectNote(event.target.value)}
                          />
                          <button
                            type="button"
                            className="btn btn-danger"
                            disabled={busyId === row.id}
                            onClick={() => reject(row)}
                          >
                            Confirm reject
                          </button>
                        </div>
                      </td>
                    </tr>
                  )}
                </React.Fragment>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
